import type { SimulationState, ThemeData, Staff, ProjectPhase } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

/**
 * Phase weights: which stats matter most during each phase.
 * Values are multipliers on the base stat weight from the category.
 */
const PHASE_STAT_EMPHASIS: Record<ProjectPhase, Record<string, number>> = {
  planning: { design: 1.5, coding: 0.5, art: 0.8, sound: 0.5 },
  alpha: { coding: 1.5, design: 0.8, art: 0.7, sound: 0.5 },
  beta: { coding: 1.0, art: 1.3, design: 1.0, sound: 0.8 },
  polish: { art: 1.5, sound: 1.3, design: 1.0, coding: 0.5 },
  debug: { coding: 1.5, design: 0.5, art: 0.3, sound: 0.3 },
  ready: {},    // No work happens in ready state — waiting for player decision
  complete: {},
};

/** Phases in order, with the fraction of total ticks each phase gets. */
const PHASE_SEQUENCE: { phase: ProjectPhase; fraction: number }[] = [
  { phase: 'planning', fraction: 0.10 },
  { phase: 'alpha', fraction: 0.30 },
  { phase: 'beta', fraction: 0.25 },
  { phase: 'polish', fraction: 0.20 },
  { phase: 'debug', fraction: 0.15 },
];

function getEnergyModifier(energy: number): number {
  // 1.0 at 100, diminishing curve down to 0.3 at 0
  return 0.3 + 0.7 * (energy / 100);
}

function getMoraleModifier(morale: number): number {
  // 1.0 at 100, down to 0.5 at 0
  return 0.5 + 0.5 * (morale / 100);
}

function getTraitStatMultiplier(staff: Staff, stat: string, themeData: ThemeData): number {
  let multiplier = 1.0;
  for (const traitId of staff.traits) {
    const traitDef = themeData.entities.traits.find((t) => t.id === traitId);
    if (!traitDef) continue;
    for (const effect of traitDef.effects) {
      if (effect.type === 'STAT_MODIFIER') {
        if (effect.stat === '*' || effect.stat === stat) {
          multiplier *= effect.multiplier;
        }
      }
    }
  }
  return multiplier;
}

function getSeniorRoleStatBonus(staff: Staff, stat: string, themeData: ThemeData): number {
  if (!staff.seniorRole || !themeData.entities.seniorRoles) return 1.0;
  const role = themeData.entities.seniorRoles.find((r) => r.id === staff.seniorRole);
  if (!role) return 1.0;
  return role.statBonus[stat] ?? 1.0;
}

function getSeniorRoleBugReduction(staff: Staff, themeData: ThemeData): number {
  if (!staff.seniorRole || !themeData.entities.seniorRoles) return 1.0;
  const role = themeData.entities.seniorRoles.find((r) => r.id === staff.seniorRole);
  return role?.bugReduction ?? 1.0;
}

function getTraitPhaseMultiplier(staff: Staff, phase: ProjectPhase, themeData: ThemeData): number {
  let multiplier = 1.0;
  for (const traitId of staff.traits) {
    const traitDef = themeData.entities.traits.find((t) => t.id === traitId);
    if (!traitDef) continue;
    for (const effect of traitDef.effects) {
      if (effect.type === 'PHASE_BONUS' && effect.phase === phase) {
        multiplier *= effect.multiplier;
      }
    }
  }
  return multiplier;
}

function isTeamLead(staff: Staff, state: SimulationState): boolean {
  if (!staff.teamId) return false;
  const team = state.teams.find((t) => t.id === staff.teamId);
  return team?.leaderId === staff.id;
}

function hasNaturalLeaderTrait(staff: Staff): boolean {
  return staff.traits.includes('natural_leader');
}

function getTeamLeadBonus(team: { leaderId: string | null }, state: SimulationState): number {
  if (!team.leaderId) return 1.0;
  const leader = state.staff.find((s) => s.id === team.leaderId);
  if (!leader) return 1.0;
  return hasNaturalLeaderTrait(leader) ? 1.25 : 1.15;
}

export function productionSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];

  for (const project of state.projects) {
    if (project.phase === 'complete') continue;

    // Find the team working on this project
    const team = state.teams.find((t) => t.assignedProjectId === project.id);
    if (!team || team.memberIds.length === 0) continue;

    // Get category stat weights
    const category = themeData.entities.projectCategories.find((c) => c.id === project.category);
    if (!category) continue;

    const phaseEmphasis = PHASE_STAT_EMPHASIS[project.phase] ?? {};
    const teamLeadBonus = getTeamLeadBonus(team, state);

    // Each staff member contributes
    for (const memberId of team.memberIds) {
      const staff = state.staff.find((s) => s.id === memberId);
      if (!staff) continue;
      if (staff.isTraining || staff.isOnLeave) continue;

      const isLead = isTeamLead(staff, state);
      const leadContributionPenalty = isLead ? 0.7 : 1.0;
      const memberBonus = isLead ? 1.0 : teamLeadBonus;

      for (const [stat, weight] of Object.entries(category.statWeights)) {
        const baseStat = staff.stats[stat] ?? 0;
        const levelScale = 1 + staff.level * 0.1;
        const energyMod = getEnergyModifier(staff.energy);
        const moraleMod = getMoraleModifier(staff.morale);
        const traitMod = getTraitStatMultiplier(staff, stat, themeData);
        const phaseMod = getTraitPhaseMultiplier(staff, project.phase, themeData);
        const phaseWeight = phaseEmphasis[stat] ?? 1.0;
        const equipMod = staff.equipmentBonus;
        const seniorMod = getSeniorRoleStatBonus(staff, stat, themeData);
        const variance = rng.nextFloat(0.8, 1.2);

        const contribution =
          baseStat *
          levelScale *
          weight *
          phaseWeight *
          energyMod *
          moraleMod *
          traitMod *
          phaseMod *
          leadContributionPenalty *
          memberBonus *
          equipMod *
          seniorMod *
          variance *
          0.1;

        if (contribution > 0) {
          project.progress[stat] = (project.progress[stat] ?? 0) + contribution;
          staff.lifetimeContribution += contribution;
        }
      }

      // Bugs accumulate — every contributor adds bugs
      const codingStat = Math.max(staff.stats['coding'] ?? 10, 1);
      // Base bug rate scales inversely with coding skill, but never drops below 40% of base
      let bugRate = themeData.balance.bugBaseRate * Math.max(0.4, 20 / codingStat);

      // Speed demon: more bugs
      if (staff.traits.includes('speed_demon')) bugRate *= 1.3;
      // Perfectionist / code_wizard: fewer bugs
      if (staff.traits.includes('perfectionist')) bugRate *= 0.8;
      if (staff.traits.includes('code_wizard')) bugRate *= 0.65;

      // Senior role bug reduction (e.g., QA Lead)
      bugRate *= getSeniorRoleBugReduction(staff, themeData);

      if (project.phase !== 'debug') {
        project.bugs += bugRate * rng.nextFloat(0.7, 1.5);
      } else {
        // Debug phase: reduce bugs, but not trivially — each bug takes real effort
        const debugSkill = (staff.stats['coding'] ?? 10) / 30;
        let bugFix = debugSkill * 0.35 * rng.nextFloat(0.6, 1.2);
        if (staff.traits.includes('debug_master')) bugFix *= 1.8;
        project.bugs = Math.max(0, project.bugs - bugFix);
      }

      // Energy drain
      staff.energy = Math.max(0, staff.energy - themeData.balance.energyDrainPerTick);
    }

    // Update phase progress — driven by cumulative stat contributions, not raw ticks
    const totalStatProgress = Object.values(project.progress).reduce((a, b) => a + b, 0);
    const currentPhaseIndex = PHASE_SEQUENCE.findIndex((p) => p.phase === project.phase);
    if (currentPhaseIndex === -1) continue;

    const phaseInfo = PHASE_SEQUENCE[currentPhaseIndex]!;
    project.ticksInPhase++;

    // Progress target per phase: fraction of the total expected contribution
    const sizeProgressTarget: Record<string, number> = { small: 800, medium: 2500, large: 6000, aaa: 14000 };
    let totalProgressTarget = sizeProgressTarget[project.size] ?? 800;
    // Each "Debug More" cycle extends the total target so the debug phase has real work to do
    const extraCycles = project.debugCycles ?? 0;
    if (extraCycles > 0) {
      totalProgressTarget += totalProgressTarget * 0.15 * extraCycles; // +15% per debug cycle
    }
    const phaseProgressTarget = totalProgressTarget * phaseInfo.fraction;

    // How much progress was contributed in prior phases?
    const priorPhaseFraction = PHASE_SEQUENCE.slice(0, currentPhaseIndex).reduce((s, p) => s + p.fraction, 0);
    const priorPhaseTarget = totalProgressTarget * priorPhaseFraction;
    const progressInPhase = Math.max(0, totalStatProgress - priorPhaseTarget);

    project.phaseProgress = Math.min(100, (progressInPhase / phaseProgressTarget) * 100);

    // Quality: scales with team size AND average staff level so it stays challenging
    const sizeQualityBase: Record<string, number> = { small: 800, medium: 2500, large: 7000, aaa: 16000 };
    let qualityTarget = sizeQualityBase[project.size] ?? 800;
    // Higher-level teams need more progress per quality point (expectations rise)
    const members = team.memberIds.map((id) => state.staff.find((s) => s.id === id)).filter(Boolean);
    const avgLevel = members.length > 0 ? members.reduce((s, m) => s + (m?.level ?? 1), 0) / members.length : 1;
    qualityTarget *= 1 + (avgLevel - 1) * 0.04; // +4% per average level above 1
    qualityTarget *= 1 + (members.length - 1) * 0.35; // Bigger teams need proportionally more
    project.quality = Math.min(100, (totalStatProgress / Math.max(qualityTarget, 1)) * 100);

    // Phase transition — when contributions fill the phase bar
    if (progressInPhase >= phaseProgressTarget) {
      const nextPhaseIndex = currentPhaseIndex + 1;
      if (nextPhaseIndex < PHASE_SEQUENCE.length) {
        project.phase = PHASE_SEQUENCE[nextPhaseIndex]!.phase;
        project.ticksInPhase = 0;
        project.phaseProgress = 0;
        events.push({
          type: 'PHASE_CHANGED',
          projectId: project.id,
          name: project.name,
          newPhase: project.phase,
        });
      } else {
        // Debug phase finished — enter 'ready' state for player decision
        project.phase = 'ready';
        project.phaseProgress = 100;

        events.push({
          type: 'PROJECT_READY_TO_SHIP',
          projectId: project.id,
          name: project.name,
          quality: Math.round(project.quality),
          bugs: Math.round(project.bugs),
        });
      }
    }
  }

  // Energy recovery for idle staff — boosted by office perks
  let energyRecoveryMult = 1.0;
  if (themeData.entities.perkEffects) {
    for (const perk of state.office.perks) {
      const effect = themeData.entities.perkEffects[perk];
      if (effect) energyRecoveryMult += effect.energyRecoveryBonus;
    }
  }

  for (const staff of state.staff) {
    if (staff.isTraining || staff.isOnLeave) continue;

    const isWorking = staff.teamId
      ? state.teams.some(
          (t) => t.id === staff.teamId && t.assignedProjectId !== null,
        )
      : false;

    if (!isWorking) {
      staff.energy = Math.min(100, staff.energy + themeData.balance.energyRecoveryPerTick * energyRecoveryMult);
    }
  }

  return events;
}
